// 2026-06-25: Building Add dialog with 3 collapsible sections (Geography / Plant / Building).
import { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, TextField, Button, IconButton, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Swal from 'sweetalert2';
import MapPicker from '../../Shared/MapPicker';
import './buildings.css';
import { shiftadd } from '../../Services/app/masterservice';

const CONTINENTS = ['Asia', 'Europe', 'Africa', 'North America', 'South America', 'Oceania'];
const COUNTRIES_BY_CONTINENT = {
  Asia: ['India', 'China', 'Japan', 'Thailand', 'Vietnam', 'South Korea', 'Singapore', 'UAE'],
  Europe: ['Germany', 'United Kingdom', 'France', 'Italy', 'Spain', 'Poland', 'Czech Republic'],
  'North America': ['United States', 'Canada', 'Mexico'],
  'South America': ['Brazil', 'Argentina', 'Chile'],
  Africa: ['South Africa', 'Egypt', 'Morocco'],
  Oceania: ['Australia', 'New Zealand'],
};
const STATES_BY_COUNTRY = {
  India: ['Tamil Nadu', 'Karnataka', 'Maharashtra', 'Gujarat', 'Haryana', 'Punjab', 'Delhi', 'Telangana', 'Andhra Pradesh', 'Kerala', 'Rajasthan', 'Uttar Pradesh', 'Madhya Pradesh', 'West Bengal'],
};

export default function BuildingsAdd({ open, handleClose, handleAdd, dialogOpenCount, datasource, customerId }) {
  const dialogBackgroundColor = dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';

  // Pre-fill Geography + Plant from first existing building (since usually same)
  const seed = useMemo(() => {
    const last = (datasource || []).slice(-1)[0] || {};
    return {
      continent: last.continent || 'Asia',
      country: last.country || 'India',
      state: last.state || 'Tamil Nadu',
      plant_code: last.plant_code || '',
      plant_name: last.plant_name || '',
      plant_city: last.plant_city || '',
      plant_lat: last.plant_lat || '',
      plant_lng: last.plant_lng || '',
    };
  }, [datasource]);

  const [form, setForm] = useState({
    code: '',
    name: '',
    floor_count: 1,
    address: '',
    gate_number: '',
    utility_meter_ids: '',
    evacuation_zone: '',
    sort_order: (datasource?.length || 0) + 1,
    active: true,
    ...seed,
  });

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
  const setVal = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const countries = COUNTRIES_BY_CONTINENT[form.continent] || [];
  const states = STATES_BY_COUNTRY[form.country] || [];

  const onSubmit = async () => {
    if (!form.code) {
      Swal.fire('Missing field', 'Code is required (e.g., BLDG-A)', 'warning');
      return;
    }
    if (!form.name) {
      Swal.fire('Missing field', 'Name is required', 'warning');
      return;
    }
    // Duplicate code check
    if ((datasource || []).some(b => String(b.code).toLowerCase() === String(form.code).toLowerCase())) {
      Swal.fire('Duplicate code', `Building code "${form.code}" already exists.`, 'error');
      return;
    }
    const now = Date.now();
    const newBuilding = {
      id: `bldg-${now}-${Math.floor(Math.random() * 9999)}`,
      ...form,
      utility_meter_ids: form.utility_meter_ids
        ? form.utility_meter_ids.split(',').map(s => s.trim()).filter(Boolean)
        : [],
      floor_count: Number(form.floor_count) || 1,
      plant_lat: form.plant_lat ? Number(form.plant_lat) : null,
      plant_lng: form.plant_lng ? Number(form.plant_lng) : null,
      sort_order: Number(form.sort_order) || 0,
      created_at: now,
      updated_at: now,
    };
    try {
      const updated = [...(datasource || []), newBuilding];
      await shiftadd({ buildings: updated }, customerId, 'SERVER_SCOPE');
      Swal.fire({ icon: 'success', title: 'Site added', timer: 1100, showConfirmButton: false });
      handleAdd();
    } catch (e) {
      console.error('Building add error', e);
      Swal.fire('Error', e?.response?.data?.message || e.message || 'Save failed', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ style: { background: dialogBackgroundColor } }}>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Add Site</span>
          <Tooltip title="Close"><IconButton onClick={handleClose}><CloseIcon /></IconButton></Tooltip>
        </div>
      </DialogTitle>

      <div style={{ padding: '8px 24px 24px' }}>
        {/* Section 1: Geography (collapsed when adding 2nd+ building) */}
        <Accordion defaultExpanded={(datasource?.length || 0) === 0} sx={{ mb: 1, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <strong>Geography</strong>
            <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 12 }}>
              {form.continent} · {form.country} · {form.state}
            </span>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Continent</InputLabel>
                <Select label="Continent" value={form.continent} onChange={set('continent')}>
                  {CONTINENTS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Country</InputLabel>
                <Select label="Country" value={form.country} onChange={set('country')}>
                  {countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>State</InputLabel>
                <Select label="State" value={form.state} onChange={set('state')}>
                  {states.length === 0 && <MenuItem value={form.state}>{form.state || '—'}</MenuItem>}
                  {states.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Section 2: Plant (collapsed when 2nd+ building) */}
        <Accordion defaultExpanded={(datasource?.length || 0) === 0} sx={{ mb: 1, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <strong>Plant Info</strong>
            <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 12 }}>
              {form.plant_name || '(unnamed)'} · {form.plant_city || ''}
            </span>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <TextField size="small" label="Plant Code" placeholder="PMI-BLR-01" value={form.plant_code} onChange={set('plant_code')} fullWidth />
              <TextField size="small" label="Plant Name" placeholder="PMI Bangalore" value={form.plant_name} onChange={set('plant_name')} fullWidth />
              <TextField size="small" label="City" placeholder="Bangalore" value={form.plant_city} onChange={set('plant_city')} fullWidth />
              <div /> {/* spacer to keep 2-col grid balanced */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>
                  Plant Location (click on map to set, or use search)
                </label>
                <MapPicker
                  lat={form.plant_lat}
                  lng={form.plant_lng}
                  onChange={(la, lo) => { setVal('plant_lat', la); setVal('plant_lng', lo); }}
                  onAddress={(addr) => {
                    if (addr.city)    setVal('plant_city', addr.city);
                    if (addr.state)   setVal('state', addr.state);
                    if (addr.country) setVal('country', addr.country);
                  }}
                  height={280}
                  searchHint={form.plant_city ? form.plant_city + ', ' + (form.country || 'India') : 'Search city or address...'}
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Section 3: Building Details (always expanded) */}
        <Accordion defaultExpanded sx={{ mb: 1, boxShadow: 'none', border: '1.5px solid #f97316' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <strong style={{ color: '#f97316' }}>Building Details</strong>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <TextField size="small" label="Code *" placeholder="BLDG-A" value={form.code} onChange={set('code')} required fullWidth />
              <TextField size="small" label="Name *" placeholder="Production Building" value={form.name} onChange={set('name')} required fullWidth />
              <FormControl size="small" fullWidth>
                <InputLabel>Floor count</InputLabel>
                <Select label="Floor count" value={form.floor_count} onChange={set('floor_count')}>
                  {[1, 2, 3, 4, 5, 6].map(n => <MenuItem key={n} value={n}>{n} floor{n > 1 ? 's' : ''}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="Gate Number" placeholder="Gate 1" value={form.gate_number} onChange={set('gate_number')} fullWidth />
              <TextField size="small" label="Address" placeholder="Plot 12, Hosur Road" value={form.address} onChange={set('address')} fullWidth style={{ gridColumn: 'span 2' }} />
              <TextField size="small" label="Utility Meter IDs (comma-separated)" placeholder="EB-MTR-12, AIR-MTR-3" value={form.utility_meter_ids} onChange={set('utility_meter_ids')} fullWidth />
              <TextField size="small" label="Evacuation Zone" placeholder="Zone-1" value={form.evacuation_zone} onChange={set('evacuation_zone')} fullWidth />
              <TextField size="small" label="Sort order" type="number" value={form.sort_order} onChange={set('sort_order')} fullWidth />
            </div>
          </AccordionDetails>
        </Accordion>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={onSubmit} variant="contained" style={{ background: '#f97316' }}>Save Building</Button>
        </div>
      </div>
    </Dialog>
  );
}
